import { memo, useEffect, useState } from "react"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material"
import { useActiveProfile } from "../profiles"
import { useDeleteAccount } from "./useDeleteAccount"
import { ReportProblemOutlined } from "@mui/icons-material"
import { CancelButton } from "../common/forms/CancelButton"
import { ErrorAlert } from "../errors/ErrorMessage"

const CONFIRMATION_PHRASE = "delete my account"

type DeleteAccountDialogProps = {
  open: boolean
  onClose: () => void
}

export const DeleteAccountDialog = memo(function DeleteAccountDialog({
  onClose,
  open,
}: DeleteAccountDialogProps) {
  const { data: auth } = useActiveProfile()
  const email = auth?.email ?? ""
  const [confirmationInput, setConfirmationInput] = useState("")
  const { mutate, isPending, isError, error } = useDeleteAccount()
  const isConfirmed =
    confirmationInput.trim().toLowerCase() === CONFIRMATION_PHRASE

  useEffect(() => {
    if (open) {
      setConfirmationInput("")
    }
  }, [open])

  return (
    <Dialog onClose={onClose} open={open}>
      <DialogTitle
        sx={{
          color: "error.main",
          gap: 1,
          display: "flex",
          alignItems: "center",
        }}
      >
        <ReportProblemOutlined color="error" fontSize="large" />
        Delete my account
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: "error.main" }}>
          {`This will permanently delete your account (${email}) and all associated data including books, collections, tags, reading progress, and data source configurations. This action cannot be undone.`}
        </DialogContentText>
        <DialogContentText
          sx={{
            mt: 2,
          }}
        >
          {`Type "${CONFIRMATION_PHRASE}" to confirm.`}
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          value={confirmationInput}
          onChange={(e) => setConfirmationInput(e.target.value)}
          disabled={isPending}
          placeholder={CONFIRMATION_PHRASE}
        />
        {isError && (
          <DialogContentText
            color="error"
            sx={{
              mt: 1,
            }}
          >
            <ErrorAlert error={error} />
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onClose} disabled={isPending} />
        <Button
          onClick={() => mutate()}
          color="error"
          disabled={!isConfirmed || isPending}
        >
          {isPending ? "Deleting..." : "Delete my account"}
        </Button>
      </DialogActions>
    </Dialog>
  )
})
